"use client"

import * as React from "react"

import { cn } from "@/lib/utils"
import {
  Group,
  Panel,
  Separator,
  type SeparatorProps,
} from "react-resizable-panels"

const ResizablePanelGroup = ({
  className,
  ...props
}: React.ComponentProps<typeof Group>) => (
  <Group
    aria-orientation={props.orientation ?? 'horizontal'}
    className={cn("flex h-full w-full aria-[orientation=vertical]:flex-col", className)}
    {...props}
  />
)

const ResizablePanel = Panel

const ResizableHandle = ({
  withHandle,
  collapsedVisual,
  className,
  ...props
}: SeparatorProps & {
  withHandle?: boolean
  collapsedVisual?: boolean
}) => (
  <Separator
    className={cn(
      "relative z-20 group flex w-px shrink-0 cursor-col-resize items-center justify-center bg-border min-w-0 max-w-px outline-none focus:outline-none focus:ring-0",
      "aria-[orientation=horizontal]:h-px aria-[orientation=horizontal]:w-full aria-[orientation=horizontal]:min-h-0 aria-[orientation=horizontal]:max-h-px aria-[orientation=horizontal]:shrink-0 aria-[orientation=horizontal]:cursor-row-resize",
      collapsedVisual && "bg-transparent",
      className
    )}
    {...props}
  >
    {/* Invisible hit target — centered on the separator so grab is equal on
        both panes. z-20 keeps this above both panes. Agent thread scrollport
        inset (chat-pane) keeps the scrollbar clear of this hit area. */}
    <div
      aria-hidden
      className={cn(
        "absolute inset-y-0 left-1/2 w-5 -translate-x-1/2",
        "group-aria-[orientation=horizontal]:inset-x-0 group-aria-[orientation=horizontal]:inset-y-auto",
        "group-aria-[orientation=horizontal]:top-1/2 group-aria-[orientation=horizontal]:left-0",
        "group-aria-[orientation=horizontal]:h-5 group-aria-[orientation=horizontal]:w-full",
        "group-aria-[orientation=horizontal]:-translate-y-1/2 group-aria-[orientation=horizontal]:translate-x-0",
        collapsedVisual && "hidden"
      )}
    />
    {withHandle ? (
      <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center group-aria-[orientation=horizontal]:rotate-90">
        <div className="h-28 w-1 shrink-0 rounded-full bg-muted-foreground/50 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100" />
      </div>
    ) : null}
  </Separator>
)

export { ResizablePanelGroup, ResizablePanel, ResizableHandle }
